import { useState, useEffect } from 'react';
import axios from 'axios';
import { clinicalServices } from '../services/endpoints/clinical';
import { InvestigationComparisonRow } from '../InvestigationComparisonTable';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export const useInvestigationComparison = (
    startDate: number | null,
    endDate: number | null,
) => {
    const [data, setData] = useState<InvestigationComparisonRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    const retry = () => setReloadToken(t => t + 1);

    useEffect(() => {
        const source = axios.CancelToken.source();
        const superseded = () => Boolean(source.token.reason);

        const fetchData = async () => {
            const end = endDate ?? Date.now();
            const start = startDate ?? end - THIRTY_DAYS_MS;
            setLoading(true);
            setError(null);
            try {
                const rows = await clinicalServices.getInvestigationComparison(
                    start,
                    end,
                    source.token,
                );
                if (superseded()) return;
                setData(rows);
            } catch (err) {
                if (axios.isCancel(err) || superseded()) return;
                setError(err instanceof Error ? err : new Error('Unknown error'));
            } finally {
                if (!superseded()) setLoading(false);
            }
        };

        fetchData();
        return () => source.cancel('superseded');
    }, [startDate, endDate, reloadToken]);

    return { data, loading, error, retry };
};
